import React from "react";
import { Flex, Box, Text } from "rebass/styled-components";
import Heading from "../common/Heading";
import Icon from "../common/Icon";
import useTranslation from "next-translate/useTranslation";
import { DayPeriod, Dog } from "../../generated/graphql";

interface Props {
  dog: Omit<Dog, "user" | "avatar" | "reviews" | "sameBreedDogs">;
}

interface PeriodProps {
  periods: DayPeriod;
}

const DayPeriods = ({ periods }: PeriodProps) => {
  const { t } = useTranslation("dogprofile");
  const fontSizes = { morning: "24px", afternoon: "32px", night: "20px" };

  return (
    <Flex justifyContent="space-between">
      {Object.entries(periods).map(
        ([key, val]) =>
          key != "__typename" && (
            <Box
              key={key}
              sx={{
                color: !val && "darkGray",
                border: "default",
                borderRadius: "default",
                borderColor: val ? "springGreen" : "darkGray",
                padding: 1,
                flexBasis: "32%",
                backgroundColor: !val && "lightGray"
              }}
            >
              <Flex
                justifyContent="center"
                fontSize={fontSizes[key]}
                pt={3}
                sx={{
                  position: "relative",
                  svg: {
                    position: "absolute",
                    top: key == "afternoon" ? -1 : 0
                  }
                }}
              >
                <Icon name={key} color={val ? "springGreen" : "darkGray"} />
              </Flex>
              <Text textAlign="center">{t(key)}</Text>
            </Box>
          )
      )}
    </Flex>
  );
};

const CareInfo = ({ dog }: Props) => {
  const { t } = useTranslation("dogprofile");

  const {
    healthInfo,
    dailyFood,
    dailyFoodServings,
    dailyWalks,
    walkingInstructions,
    activityLevel,
    homeAlone,
    indoorPrecautions
  } = dog;

  const dietaryPresent = !!(!!dailyFood || dailyFoodServings);
  const indoorCarePresent = !!(activityLevel || homeAlone);
  const outdoorCarePresent = !!(dailyWalks || walkingInstructions);

  if (!indoorCarePresent && !dietaryPresent && !outdoorCarePresent) {
    return <></>;
  }

  return (
    <Flex
      px={[2, 2, 0]}
      sx={{
        flexDirection: "column",
        mt: 3
      }}
    >
      <Heading as="h2" mb={2}>
        {t("careInfo")}
      </Heading>

      {dietaryPresent && (
        <Heading as="h3" mb={2}>
          {t("dietary")}
        </Heading>
      )}
      {dailyFood && (
        <Box mb={2}>
          <Text fontWeight="book" mb={1}>
            {t("dailyServing")}
          </Text>
          <Text>{dailyFood}</Text>
        </Box>
      )}

      {dietaryPresent && (
        <Box mb={2}>
          <Text fontWeight="book" mb={1}>
            {t("dailyServingSchedule")}
          </Text>
          <DayPeriods periods={dailyFoodServings} />
        </Box>
      )}
      {healthInfo && (
        <>
          <Heading as="h3" mb={2}>
            {t("health")}
          </Heading>
          <Box mb={2}>
            <Text
              sx={{
                wordWrap: "break-word",
                whiteSpace: "pre-line"
              }}
            >
              {healthInfo}
            </Text>
          </Box>
        </>
      )}
      {outdoorCarePresent && (
        <Heading as="h3" mb={2} mt={1}>
          {t("outdoorActivities")}
        </Heading>
      )}
      {dailyWalks && (
        <Box mb={2}>
          <Text fontWeight="book" mb={1}>
            {t("dailyWalkingSchedule")}
          </Text>
          <DayPeriods periods={dailyWalks} />
        </Box>
      )}
      {walkingInstructions && (
        <Box mb={2}>
          <Text fontWeight="book" mb={1}>
            {t("walkingInstructions")}
          </Text>
          <Text
            sx={{
              wordWrap: "break-word",
              whiteSpace: "pre-line"
            }}
          >
            {walkingInstructions}
          </Text>
        </Box>
      )}
      {indoorCarePresent && (
        <Heading as="h3" mb={2} mt={1}>
          {t("indoorCare")}
        </Heading>
      )}
      {activityLevel && (
        <Flex mb={1}>
          <Text fontWeight="book" mb={1} mr={1}>
            {t("activityLevel")}:
          </Text>
          <Text>{t(activityLevel)}</Text>
        </Flex>
      )}
      {homeAlone && (
        <Box mb={2}>
          <Text fontWeight="book" mb={1}>
            {t("homeAlone")}
          </Text>
          <Text
            sx={{
              wordWrap: "break-word",
              whiteSpace: "pre-line"
            }}
          >
            {homeAlone}
          </Text>
        </Box>
      )}
      {indoorPrecautions && (
        <Box mb={2}>
          <Heading as="h3" mb={2} mt={1}>
            {t("indoorPrecautions")}
          </Heading>
          <Text
            sx={{
              wordWrap: "break-word",
              whiteSpace: "pre-line"
            }}
          >
            {indoorPrecautions}
          </Text>
        </Box>
      )}
    </Flex>
  );
};

export default CareInfo;
